import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { DemoAiNote } from './demo';
import { Alert, PageHead, PanelShell } from '../layout';

interface Credit { month: string; allowanceCents: number; usedMillicents: number; remainingMillicents: number; enabled: boolean; topups: number }
interface Ai { available: boolean; credit: Credit; conversations: { id: number; title: string; updatedAt: string }[]; topup: { cents: number; available: boolean } }
interface Message { id?: number; role: 'user' | 'assistant'; text: string; actions: { type: string; id: number; label: string }[]; quick?: boolean }
type Quick = 'debts' | 'month' | 'deadlines';

const QUICK_LABELS: Record<Quick, string> = { debts: 'Kas man skolingas?', month: 'Šio mėnesio suvestinė', deadlines: 'Artimiausi terminai' };

/** The credit meter: used of this month's credit, in euros (a little over a cent is shown as a cent). */
export function CreditMeter({ credit }: { credit: Credit }) {
  const { t } = useI18n();
  const used = Math.ceil(credit.usedMillicents / 1000);
  const share = credit.allowanceCents ? Math.min(100, Math.round((used / credit.allowanceCents) * 100)) : 100;
  return (
    <div className="credit-meter" title={t('Kreditas galioja iki mėnesio pabaigos; likutis neperkeliamas.')}>
      <div className="credit-bar"><i style={{ width: `${share}%` }} className={share >= 80 ? 'is-low' : undefined} /></div>
      <span>{t('Panaudota {used} iš {all}', { used: money(used), all: money(credit.allowanceCents) })}</span>
    </div>
  );
}

/** AI asistentas: questions about the company's invoices, debts, the month and taxes; invoice drafts. */
export function AssistantPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['ai'], queryFn: () => api.get<Ai>('api/v1/ai') });
  const [conversation, setConversation] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  const open = async (id: number) => {
    const r = await api.get<{ messages: Message[] }>(`api/v1/ai/conversations/${id}`);
    setConversation(id); setMessages(r.messages);
  };
  const send = useMutation({
    mutationFn: (message: string) => api.post<{ conversationId: number; reply: string; actions: Message['actions']; credit: Credit }>('api/v1/ai/chat', { conversationId: conversation, message }),
    onMutate: (message) => setMessages((m) => [...m, { role: 'user', text: message, actions: [] }]),
    onSuccess: (r) => {
      setConversation(r.conversationId);
      setMessages((m) => [...m, { role: 'assistant', text: r.reply, actions: r.actions }]);
      queryClient.setQueryData(['ai'], (old: Ai | undefined) => (old ? { ...old, credit: r.credit } : old));
      void queryClient.invalidateQueries({ queryKey: ['ai'] });
      if (r.actions.length) void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
  // Quick answers: straight from the company's reports, without the AI and without using credit.
  const quick = useMutation({
    mutationFn: async (kind: Quick): Promise<string> => {
      if (kind === 'debts') {
        const r = await api.get<{ total: number; overdue: number; customers: { name: string; outstanding: number; overdue: number; maxDaysOverdue: number }[] }>('api/v1/reports/receivables');
        if (!r.customers.length) return t('Skolų nėra — visos sąskaitos apmokėtos.');
        return [t('Iš viso skolingi: {total}, iš jų vėluoja {overdue}.', { total: money(r.total), overdue: money(r.overdue) }),
          ...r.customers.slice(0, 5).map((c) => `• ${c.name} — ${money(c.outstanding)}${c.maxDaysOverdue > 0 ? ` (${t('vėluoja iki {n} d.', { n: c.maxDaysOverdue })})` : ''}`)].join('\n');
      }
      if (kind === 'month') {
        const m = await api.get<{ month: string; issued: { count: number; net: number; vat: number; total: number }; paid: { count: number; total: number }; unpaid: number; received: { count: number; total: number; vat: number } }>('api/v1/reports/month');
        return [t('Mėnuo {month}:', { month: m.month }),
          `• ${t('Išrašyta {n} sąsk. už {sum} (PVM {vat})', { n: m.issued.count, sum: money(m.issued.total), vat: money(m.issued.vat) })}`,
          `• ${t('Apmokėta {n} sąsk. už {sum}', { n: m.paid.count, sum: money(m.paid.total) })}`,
          `• ${t('Dar neapmokėta: {sum}', { sum: money(m.unpaid) })}`,
          `• ${t('Gauta {n} sąsk. už {sum} (PVM {vat})', { n: m.received.count, sum: money(m.received.total), vat: money(m.received.vat) })}`].join('\n');
      }
      const x = await api.get<{ items: { due: string; title: string; period: string; done: unknown; daysLeft: number; vat?: { toPay: number }; amount?: number }[] }>('api/v1/taxes');
      const next = x.items.filter((i) => !i.done && i.daysLeft >= -30).slice(0, 5);
      if (!next.length) return t('Artimiausių terminų nėra (pagal Mokesčių kalendoriaus nustatymus).');
      return [t('Artimiausi terminai:'), ...next.map((i) => `• ${i.due} — ${t(i.title)} (${i.period})${i.vat ? ` · ${t('PVM mokėti: {sum}', { sum: money(Math.max(0, i.vat.toPay)) })}` : ''}${i.amount !== undefined ? ` · ${t('Mokėti: {sum}', { sum: money(i.amount) })}` : ''}${i.daysLeft < 0 ? ` · ${t('Praleista {n} d.', { n: -i.daysLeft })}` : ''}`)].join('\n');
    },
    onSuccess: (text, kind) => setMessages((m) => [...m, { role: 'user', text: t(QUICK_LABELS[kind]), actions: [] }, { role: 'assistant', text, actions: [], quick: true }]),
  });
  const topup = useMutation({ mutationFn: () => api.post<{ url: string }>('api/v1/ai/topup'), onSuccess: (r) => window.location.assign(r.url) });
  const submit = (e: FormEvent) => { e.preventDefault(); const m = input.trim(); if (!m || send.isPending) return; setInput(''); send.mutate(m); };
  const d = data.data;
  const low = d ? d.credit.remainingMillicents < d.credit.allowanceCents * 200 : false; // under 20 % left
  const owner = me.company.role === 'owner' && !me.viewAsAdmin;
  const done = new URLSearchParams(window.location.search).get('topup') === 'done';
  const examples = ['Kas man skolingas ir kiek vėluoja?', 'Kokia šio mėnesio suvestinė?', 'Kokie artimiausi mokesčių terminai?', 'Paruošk sąskaitą klientui už konsultaciją 2 val. po 50 €'];
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Priedai')} title={t('AI asistentas')} text={t('Klauskite apie savo sąskaitas, skolas, mėnesį ir terminus. Asistentas gali paruošti sąskaitos juodraštį — jo niekada neišsiunčia.')} />}>
      {me.demo ? <DemoAiNote /> : null}
      {done ? <Alert kind="success">{t('Ačiū! Kreditas papildytas — jis atsiras per kelias sekundes.')}</Alert> : null}
      {data.isError || send.isError || topup.isError || quick.isError ? <Alert kind="error">{errorText(t, data.error ?? send.error ?? topup.error ?? quick.error)}</Alert> : null}
      {d ? <div className="assistant-layout">
        <aside className="panel-card assistant-side">
          <CreditMeter credit={d.credit} />
          {low && owner && d.topup.available ? <button type="button" className="button button--primary button--small" disabled={topup.isPending} onClick={() => topup.mutate()}>{t('Papildyti {sum}', { sum: money(d.topup.cents) })}</button> : null}
          {low && !d.topup.available && owner ? <p className="muted">{t('Kreditas baigiasi. Papildyti galima, kai planas apmokamas kortele; arba parašykite mums.')}</p> : null}
          <button type="button" className="button button--ghost button--small" onClick={() => { setConversation(null); setMessages([]); }}>+ {t('Naujas pokalbis')}</button>
          <ul className="assistant-conversations">
            {d.conversations.map((c) => <li key={c.id}><button type="button" className={c.id === conversation ? 'active' : ''} onClick={() => void open(c.id)}>{c.title || t('Pokalbis')}</button></li>)}
          </ul>
          <p className="muted assistant-note">{t('Kreditas galioja iki mėnesio pabaigos; likutis neperkeliamas.')}</p>
        </aside>
        <section className="panel-card assistant-chat">
          <div className="assistant-messages">
            {!messages.length ? <div className="assistant-empty"><p className="muted">{t('Pavyzdžiui:')}</p>
              {examples.map((e) => <button key={e} type="button" className="assistant-example" onClick={() => setInput(t(e))}>{t(e)}</button>)}</div> : null}
            {messages.map((m, i) => (
              <div key={m.id ?? `n${i}`} className={`assistant-msg assistant-msg--${m.role}`}>
                <p>{m.text}</p>
                {m.quick ? <small className="muted">{t('Iš jūsų duomenų, be AI — nemokamai.')}</small> : null}
                {m.actions.map((a) => a.type === 'invoice' ? <Link key={a.id} className="button button--ghost button--small" to="/outgoing/$id" params={{ id: String(a.id) }}>{t('Atidaryti juodraštį')} {a.label}</Link> : null)}
              </div>
            ))}
            {send.isPending ? <div className="assistant-msg assistant-msg--assistant"><p className="muted">{t('Galvoja…')}</p></div> : null}
            <div ref={end} />
          </div>
          <div className="assistant-quick">
            {(Object.keys(QUICK_LABELS) as Quick[]).map((k) => <button key={k} type="button" className="assistant-example" disabled={quick.isPending} onClick={() => quick.mutate(k)}>⚡ {t(QUICK_LABELS[k])}</button>)}
          </div>
          <form className="assistant-input" onSubmit={submit}>
            <textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('Užduokite klausimą…')} maxLength={4000}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); } }} />
            <button className="button button--primary" disabled={send.isPending || !input.trim() || d.credit.remainingMillicents < 3000}>{t('Siųsti')}</button>
          </form>
        </section>
      </div> : null}
    </PanelShell>
  );
}
